import React, { ChangeEvent, FC, useState, useContext } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers'
import { Select, ISelectOption } from 'components/forms/Selects'
import { Autocomplete } from 'components/forms/autocomplete/DPLAutocomplete'
import ControlledCheckbox from 'transfix-form/components/ControlledCheckbox'
import ControlledRangeDatePicker from 'transfix-form/components/ControlledRangeDatePicker'
import { DeprecatedControlledSelect } from 'transfix-form/components/ControlledSelect/ControlledSelect'
import { DeprecatedControlledTextInput } from 'transfix-form/components/ControlledTextInput/ControlledTextInput'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { FormModal } from 'transfix-ui/components/Modals'
import SuggestLocation, { ILocationSuggestion } from 'transfix-ui/components/SuggestLocation'
import { Typography, Divider, Box } from 'transfix-ui/core'
import { FeatureFlagsContext } from 'utils/context/FeatureFlagsContext'
import { SpotBiddingRule } from 'apps/marketplace/apiTypes/spotBiddingConfigs'
import {
  useCreateSpotBiddingRule,
  useUpdateSpotBiddingRule,
} from 'apps/marketplace/apiTypes/spotBiddingRules'
import { TimeInput, TIME_INPUT_FORMAT } from 'transfix-ui'
import {
  dateGeoOptions,
  dateOnlyOptions,
  geoDirectionOptions,
  geoTypeOptions,
  mileageBucketOptions,
  regionOptions,
  ruleActionOptions,
  ruleTypeOptions,
} from '../constants/shipperStrikePrices'
import {
  APPOINTMENT_TYPE,
  APPOINTMENT_TYPE_OPTIONS,
  EQUIPMENT_OPTIONS,
} from '../constants/spotBiddingConfig'
import { StrikePriceRuleSchema, IStrikePriceRuleSchema } from '../schema/StrikePriceRuleSchema'
import {
  DATE_TYPES,
  GEOGRAPHIC_CRITERIA_TYPES,
  GEOGRAPHIC_DIRECTIONS,
  MILEAGE_BUCKETS,
  RULE_ACTIONS,
  RULE_TYPES,
} from '../types/shipperStrikePrices'
import { ISpotBiddingRule } from '../types/spotBiddingConfig'
import {
  percentageToFloat,
  floatToPercentage,
  getMileageBucketFromRange,
  getRangeFromMileageBucket,
} from '../utils/shipperStrikePrice'
import { NumDaysOut } from './form-components'
import { AppointmentTime } from './form-components/AppointmentTime'
import {
  CountLoadsQuotedValue,
  ICountLoadsQuotedErrors,
} from './form-components/CountLoadsQuotedValue'

interface IRuleModalProps {
  onClose: () => void
  spotBiddingRule?: SpotBiddingRule
  onRequestSuccess: () => void
  shipperId?: number
}

export const StrikePriceRuleModal: FC<IRuleModalProps> = ({
  onClose,
  onRequestSuccess,
  shipperId,
  spotBiddingRule,
}) => {
  const { getFlag } = useContext(FeatureFlagsContext)
  const { enabled: spcpStepPricingEnabled } = getFlag('spcpStepPricing')
  const [time, setTime] = useState<string>(spotBiddingRule?.ruleStartTimeCriteria?.rangeStart || '')
  const isEdit = spotBiddingRule !== undefined
  const isGlobalRule = shipperId === undefined
  const {
    error: createError,
    loading: createRequestPending,
    mutate: createRule,
  } = useCreateSpotBiddingRule({})
  const {
    error: updateError,
    loading: updateRequestPending,
    mutate: updateRule,
  } = useUpdateSpotBiddingRule({
    spotBiddingRuleId: spotBiddingRule?.id || 0,
  })
  const defaultPercentage = floatToPercentage(spotBiddingRule?.spotPremium?.amount || 0)
  const defaultRuleType = (() => {
    if (spotBiddingRule?.countLoadsQuotedCriteria) {
      return RULE_TYPES.STEP_PRICING
    }
    if (spotBiddingRule?.parentEquipmentKey) {
      return RULE_TYPES.EQUIPMENT
    }
    if (spotBiddingRule?.mileageCriteria) {
      return RULE_TYPES.MILEAGE
    }
    if (spotBiddingRule?.geographicCriteria) {
      return RULE_TYPES.GEOGRAPHIC
    }
    if (spotBiddingRule?.laneGeographicCriteria) {
      return RULE_TYPES.LANE
    }
    if (
      spotBiddingRule?.dateCriteria ||
      spotBiddingRule?.proximalHourCriteria ||
      spotBiddingRule?.isWeekend ||
      spotBiddingRule?.isSameDay ||
      spotBiddingRule?.appointmentTimeCriteria
    ) {
      return RULE_TYPES.TIME
    }
    return RULE_TYPES.GEOGRAPHIC
  })()
  const getDateType = (rule?: SpotBiddingRule) => {
    if (rule?.proximalHourCriteria) {
      if (rule?.ruleStartTimeCriteria) {
        return DATE_TYPES.NEXT_DAY
      }
      return DATE_TYPES.NUMBER_OF_DAYS_OUT
    }
    if (rule?.isSameDay) {
      return DATE_TYPES.SAME_DAY
    }
    return rule?.isWeekend ? DATE_TYPES.WEEKEND : DATE_TYPES.DATE_RANGE
  }
  const getAppointmentType = (rule?: SpotBiddingRule) => {
    return rule?.appointmentTimeCriteria?.pickupRangeStart
      ? APPOINTMENT_TYPE.PICKUP
      : APPOINTMENT_TYPE.DELIVERY
  }
  const methods = useForm({
    resolver: yupResolver(StrikePriceRuleSchema),
    defaultValues: {
      ruleAction: spotBiddingRule?.ruleAction || RULE_ACTIONS.APPLY_PREMIUM,
      ruleType: defaultRuleType,
      percentage: defaultPercentage || '',
      reason: spotBiddingRule?.reason || '',
      parentEquipmentKey: spotBiddingRule?.parentEquipmentKey,
      geographicCriteriaType:
        spotBiddingRule?.geographicCriteria?.criteriaType || GEOGRAPHIC_CRITERIA_TYPES.REGION,
      geographicCriteriaDirection: spotBiddingRule?.geographicCriteria?.isInbound
        ? GEOGRAPHIC_DIRECTIONS.INBOUND
        : GEOGRAPHIC_DIRECTIONS.OUTBOUND,
      geographicCriteriaValue: spotBiddingRule?.geographicCriteria?.value || '',
      laneGeographicCriteriaPickupType:
        spotBiddingRule?.laneGeographicCriteria?.pickupType || GEOGRAPHIC_CRITERIA_TYPES.REGION,
      laneGeographicCriteriaDeliveryType:
        spotBiddingRule?.laneGeographicCriteria?.deliveryType || GEOGRAPHIC_CRITERIA_TYPES.REGION,
      laneGeographicCriteriaPickupValue: spotBiddingRule?.laneGeographicCriteria?.pickupValue,
      laneGeographicCriteriaDeliveryValue: spotBiddingRule?.laneGeographicCriteria?.deliveryValue,
      dateCriteria: spotBiddingRule?.dateCriteria,
      proximalHourCriteria: spotBiddingRule?.proximalHourCriteria
        ? {
            rangeStart: spotBiddingRule.proximalHourCriteria.rangeStart,
            rangeEnd: spotBiddingRule.proximalHourCriteria?.rangeEnd,
          }
        : '',
      ruleStartTimeCriteria: spotBiddingRule?.ruleStartTimeCriteria
        ? {
            rangeStart: spotBiddingRule.ruleStartTimeCriteria?.rangeStart,
          }
        : '',
      appointmentTimeCriteria: spotBiddingRule?.appointmentTimeCriteria
        ? {
            deliveryRangeEnd: spotBiddingRule.appointmentTimeCriteria?.deliveryRangeEnd,
            deliveryRangeStart: spotBiddingRule.appointmentTimeCriteria?.deliveryRangeStart,
            pickupRangeEnd: spotBiddingRule.appointmentTimeCriteria?.pickupRangeEnd,
            pickupRangeStart: spotBiddingRule.appointmentTimeCriteria?.pickupRangeStart,
            timezoneOverride: spotBiddingRule.appointmentTimeCriteria?.timezoneOverride,
          }
        : '',
      countLoadsQuotedCriteria: spotBiddingRule?.countLoadsQuotedCriteria
        ? {
            rangeStart: spotBiddingRule?.countLoadsQuotedCriteria?.rangeStart,
            rangeEnd: spotBiddingRule?.countLoadsQuotedCriteria?.rangeEnd,
            pickupValue: spotBiddingRule?.countLoadsQuotedCriteria?.pickupValue,
            deliveryValue: spotBiddingRule?.countLoadsQuotedCriteria?.deliveryValue,
            isSamePickupDelivery: spotBiddingRule?.countLoadsQuotedCriteria?.isSamePickupDelivery,
            isDifferentPickupDelivery:
              spotBiddingRule?.countLoadsQuotedCriteria?.isDifferentPickupDelivery,
          }
        : '',
      dateType: getDateType(spotBiddingRule),
      includeGeo: !!spotBiddingRule?.geographicCriteria,
      includeSameDay: !!spotBiddingRule?.isSameDay,
      appointmentTime: !!spotBiddingRule?.appointmentTimeCriteria,
      appointmentType: spotBiddingRule?.appointmentTimeCriteria
        ? getAppointmentType(spotBiddingRule)
        : '',
      mileageBucket: spotBiddingRule?.mileageCriteria
        ? getMileageBucketFromRange(spotBiddingRule.mileageCriteria.rangeEnd)
        : MILEAGE_BUCKETS.CITY,
    },
  })
  const { control, errors, handleSubmit, setValue, watch } = methods
  const {
    appointmentTime,
    appointmentType,
    dateType,
    geographicCriteriaType,
    includeGeo,
    includeSameDay,
    laneGeographicCriteriaDeliveryType,
    laneGeographicCriteriaPickupType,
    ruleAction,
    ruleType,
  } = watch([
    'ruleAction',
    'ruleType',
    'geographicCriteriaType',
    'laneGeographicCriteriaPickupType',
    'laneGeographicCriteriaDeliveryType',
    'dateType',
    'includeGeo',
    'includeSameDay',
    'appointmentTime',
    'appointmentType',
  ])
  const dates: Date[] = []
  const addDate = (date: string) => {
    const [startYear, startMonth, startDay] = date.split('-').map(e => Number(e))
    dates.push(new Date(startYear, startMonth - 1, startDay))
  }
  if (spotBiddingRule?.dateCriteria?.rangeStart) addDate(spotBiddingRule.dateCriteria.rangeStart)
  if (spotBiddingRule?.dateCriteria?.rangeEnd) addDate(spotBiddingRule.dateCriteria.rangeEnd)
  const yesterday = (() => {
    const date = new Date()
    date.setDate(date.getDate() - 1)
    return date
  })()
  const formatSameDayTime = (time?: string) => {
    return time?.substr(0, 5)
  }
  const createOrUpdateRule = async (formParams: IStrikePriceRuleSchema) => {
    const requestParams = {
      spotBiddingRule: {
        shipperApplicationId: shipperId,
        namespace: 'strike_price_panel' as const,
        spotPremium:
          formParams.ruleAction === RULE_ACTIONS.APPLY_PREMIUM
            ? {
                premiumType: 'PERCENTAGE' as const,
                amount: percentageToFloat(formParams.percentage),
              }
            : undefined,
        ruleAction: formParams.ruleAction,
        isSameDay:
          formParams.dateType === DATE_TYPES.SAME_DAY ||
          (dateType === DATE_TYPES.NUMBER_OF_DAYS_OUT && includeSameDay),
        isWeekend: formParams.dateType === DATE_TYPES.WEEKEND,
        reason: formParams.reason,
        // strike price rules must have a range end value (default to same date as range start)
        dateCriteria: formParams.dateCriteria
          ? {
              rangeStart: formParams.dateCriteria?.rangeStart,
              rangeEnd: formParams.dateCriteria?.rangeEnd || formParams.dateCriteria?.rangeStart,
            }
          : undefined,
        appointmentTimeCriteria:
          dateType === DATE_TYPES.NUMBER_OF_DAYS_OUT && appointmentTime
            ? formParams.appointmentTimeCriteria
            : undefined,
        parentEquipmentKey: formParams.parentEquipmentKey,
        proximalHourCriteria:
          formParams.dateType === DATE_TYPES.NEXT_DAY
            ? {
                rangeStart: 24,
                rangeEnd: 48,
              }
            : formParams.proximalHourCriteria,
        ruleStartTimeCriteria:
          ([DATE_TYPES.SAME_DAY, DATE_TYPES.NEXT_DAY].includes(formParams.dateType) ||
            (dateType === DATE_TYPES.NUMBER_OF_DAYS_OUT && includeSameDay)) &&
          formParams.ruleStartTimeCriteria?.rangeStart
            ? {
                rangeStart: formatSameDayTime(formParams.ruleStartTimeCriteria?.rangeStart),
              }
            : undefined,
        geographicCriteriaAttributes:
          formParams.ruleType === RULE_TYPES.GEOGRAPHIC ||
          (formParams.ruleType === RULE_TYPES.MILEAGE && formParams.includeGeo)
            ? {
                criteriaType: formParams.geographicCriteriaType,
                isInbound: formParams.geographicCriteriaDirection === GEOGRAPHIC_DIRECTIONS.INBOUND,
                value: formParams.geographicCriteriaValue,
              }
            : undefined,
        laneGeographicCriteriaAttributes:
          formParams.ruleType === RULE_TYPES.LANE
            ? {
                pickupType: formParams.laneGeographicCriteriaPickupType,
                deliveryType: formParams.laneGeographicCriteriaDeliveryType,
                pickupValue: formParams.laneGeographicCriteriaPickupValue,
                deliveryValue: formParams.laneGeographicCriteriaDeliveryValue,
              }
            : undefined,
        mileageCriteria:
          formParams.ruleType === RULE_TYPES.MILEAGE
            ? getRangeFromMileageBucket(formParams.mileageBucket)
            : undefined,
        countLoadsQuotedCriteria:
          formParams.ruleType === RULE_TYPES.STEP_PRICING
            ? formParams.countLoadsQuotedCriteria
            : undefined,
      },
    }
    if (isEdit) {
      await updateRule(requestParams)
    } else {
      await createRule(requestParams)
    }
    onRequestSuccess()
  }
  const requestError = (() => {
    const error = (createError || updateError)?.data
    if (!error) return ''
    if (typeof error === 'string') return error
    return error.errors?.join(',')
  })()

  const permittedRuleTypeOptions = ruleTypeOptions.filter(option => {
    if (option.value === RULE_TYPES.STEP_PRICING) {
      return spcpStepPricingEnabled && !isGlobalRule
    }
    return true
  })

  return (
    <FormModal
      open
      title={`${isEdit ? 'Edit' : 'Add'} ${isGlobalRule ? 'Global' : 'Shipper'} Rule`}
      size={5}
      onClose={onClose}
      actions={[
        {
          label: 'Cancel',
          type: 'dismiss',
          dataTest: 'rule-modal-dismiss-button',
          action: onClose,
        },
        {
          label: 'Save',
          type: 'confirm',
          dataTest: 'rule-modal-confirm-button',
          action: handleSubmit(createOrUpdateRule),
          disabled: createRequestPending || updateRequestPending,
        },
      ]}>
      {requestError && (
        <Box mb={2}>
          <InlineMessage type={INLINE_MESSAGE_VARIANTS.ALERT} message={requestError} />
        </Box>
      )}
      <Box mb={2} display='flex'>
        <Box mr={1} flex={1}>
          <DeprecatedControlledSelect
            control={control}
            name='ruleAction'
            SelectProps={{
              label: 'Rule Action',
              options: ruleActionOptions,
              dataTest: 'rule-action',
              required: true,
              large: true,
            }}
          />
        </Box>
        <Box ml={1} flex={1}>
          <DeprecatedControlledSelect
            control={control}
            name='ruleType'
            SelectProps={{
              label: 'Rule Type',
              options: permittedRuleTypeOptions,
              dataTest: 'rule-type',
              required: true,
              large: true,
            }}
          />
        </Box>
      </Box>
      {ruleAction === RULE_ACTIONS.APPLY_PREMIUM && (
        <Box mb={2}>
          <DeprecatedControlledTextInput
            control={control}
            name='percentage'
            TextInputProps={{
              name: 'percentage',
              label: 'Adjustment Percentage',
              placeholder: '%',
              dataTest: 'percentage',
              invalid: !!errors.percentage,
              validationMessage: errors.percentage?.message,
              required: true,
            }}
          />
        </Box>
      )}
      <Box mb={2}>
        <Divider />
      </Box>
      {ruleType === RULE_TYPES.MILEAGE && (
        <>
          <Box mb={1}>
            <DeprecatedControlledSelect
              control={control}
              name='mileageBucket'
              SelectProps={{
                label: 'Mileage Bucket',
                options: mileageBucketOptions,
                dataTest: 'mileageBucket',
                required: true,
                large: true,
              }}
            />
            <Typography variant='subtitle2' color='textSecondary'>
              Note: The start of the range is inclusive and the end if exclusive
            </Typography>
          </Box>
          <Box mb={1}>
            <ControlledCheckbox
              control={control}
              defaultValue={!!spotBiddingRule?.geographicCriteria}
              name='includeGeo'
              CheckboxProps={{
                label: 'Use Geographic Criteria',
              }}
            />
          </Box>
        </>
      )}
      {ruleType === RULE_TYPES.EQUIPMENT && (
        <Box mb={2}>
          <DeprecatedControlledSelect
            control={control}
            name='parentEquipmentKey'
            SelectProps={{
              label: 'Equipment Type',
              options: EQUIPMENT_OPTIONS,
              dataTest: 'equipmentType',
              required: true,
              large: true,
              invalid: !!errors.parentEquipmentKey,
              validationMessage: errors.parentEquipmentKey?.message,
            }}
          />
        </Box>
      )}
      {(ruleType === RULE_TYPES.GEOGRAPHIC || (ruleType === RULE_TYPES.MILEAGE && includeGeo)) && (
        <Box mb={2}>
          <Box display='flex' mb={2}>
            <Box mr={1} flex={1}>
              <DeprecatedControlledSelect
                control={control}
                name='geographicCriteriaDirection'
                SelectProps={{
                  label: 'Direction',
                  options: geoDirectionOptions,
                  dataTest: 'isInbound',
                  required: false,
                  large: true,
                }}
              />
            </Box>
            <Box ml={1} flex={1}>
              <DeprecatedControlledSelect
                control={control}
                name='geographicCriteriaType'
                onChange={() => setValue('geographicCriteriaValue', '')}
                SelectProps={{
                  label: 'Criteria Type',
                  options: geoTypeOptions,
                  dataTest: 'geoType',
                  required: false,
                  large: true,
                }}
              />
            </Box>
          </Box>
          <Box mb={2}>
            <Controller
              name='geographicCriteriaValue'
              control={control}
              render={({ onChange, value }) => {
                const datMarketHandler = ({
                  fullSelection,
                }: {
                  value: number
                  fullSelection?: { name: string }
                }) => {
                  onChange(fullSelection?.name || '')
                }
                return (
                  <>
                    {geographicCriteriaType === GEOGRAPHIC_CRITERIA_TYPES.REGION && (
                      <Select
                        label='Region'
                        placeholder='Enter region'
                        options={regionOptions}
                        value={value}
                        onChange={(opt: ISelectOption) => onChange(opt.value)}
                        invalid={!!errors.geographicCriteriaValue}
                        dataTest='modal-geo-region'
                        large
                      />
                    )}
                    {geographicCriteriaType === GEOGRAPHIC_CRITERIA_TYPES.STATE && (
                      <SuggestLocation
                        id='modal-geo-state'
                        label='State'
                        placeholder='Enter state'
                        countries={['US']}
                        resultTypes={['State']}
                        value={{ state: value }}
                        onChange={(val: ILocationSuggestion) => {
                          if (val.state) {
                            onChange(val.state)
                          }
                        }}
                        errorMessage={errors.geographicCriteriaValue?.message}
                        invalid={!!errors.geographicCriteriaValue}
                        dataTest='modal-geo-state'
                        large
                      />
                    )}
                    {geographicCriteriaType === GEOGRAPHIC_CRITERIA_TYPES.CITY_STATE && (
                      <SuggestLocation
                        id='modal-geo-city-state'
                        label='City, State'
                        placeholder='Enter city, state'
                        resultTypes={['City']}
                        value={{
                          city: value?.split(', ')[0],
                          state: value?.split(', ')[1],
                        }}
                        onChange={(val: ILocationSuggestion) => {
                          if (val.city && val.state) {
                            onChange(`${val.city}, ${val.state}`)
                          }
                        }}
                        errorMessage={errors.geographicCriteriaValue?.message}
                        invalid={!!errors.geographicCriteriaValue}
                        dataTest='modal-geo-city-state'
                        large
                      />
                    )}
                    {geographicCriteriaType === GEOGRAPHIC_CRITERIA_TYPES.DAT_MARKET && (
                      <Autocomplete
                        id='modal-geo-dat-market'
                        label='DAT Market'
                        value={value}
                        type='dat_market'
                        onChange={datMarketHandler}
                        validationMessage={errors.geographicCriteriaValue?.message}
                        dataTest='modal-geo-dat-market'
                        large
                      />
                    )}
                  </>
                )
              }}
            />
          </Box>
        </Box>
      )}
      {ruleType === RULE_TYPES.LANE && (
        <>
          <Box mb={2}>
            <Box ml={1} flex={1} mb={1}>
              <DeprecatedControlledSelect
                control={control}
                name='laneGeographicCriteriaPickupType'
                onChange={() => setValue('laneGeographicCriteriaPickupValue', '')}
                SelectProps={{
                  label: 'Pickup Criteria Type',
                  options: geoTypeOptions,
                  dataTest: 'laneGeoPickupType',
                  required: false,
                  large: true,
                }}
              />
            </Box>
            <Box ml={1} flex={1} mb={1}>
              <DeprecatedControlledSelect
                control={control}
                name='laneGeographicCriteriaDeliveryType'
                onChange={() => setValue('laneGeographicCriteriaDeliveryValue', '')}
                SelectProps={{
                  label: 'Delivery Criteria Type',
                  options: geoTypeOptions,
                  dataTest: 'laneGeoDeliveryType',
                  required: false,
                  large: true,
                }}
              />
            </Box>
          </Box>
          <Box mb={2}>
            <Controller
              name='laneGeographicCriteriaPickupValue'
              control={control}
              render={({ onChange, value }) => {
                const datMarketHandler = ({
                  fullSelection,
                }: {
                  value: number
                  fullSelection?: { name: string }
                }) => {
                  onChange(fullSelection?.name || '')
                }
                return (
                  <>
                    {laneGeographicCriteriaPickupType === GEOGRAPHIC_CRITERIA_TYPES.REGION && (
                      <Select
                        label='Pickup Region'
                        placeholder='Enter region'
                        options={regionOptions}
                        value={value}
                        onChange={(opt: ISelectOption) => onChange(opt.value)}
                        invalid={!!errors.laneGeographicCriteriaPickupValue}
                        dataTest='modal-lane-geo-region'
                        large
                      />
                    )}
                    {laneGeographicCriteriaPickupType === GEOGRAPHIC_CRITERIA_TYPES.STATE && (
                      <SuggestLocation
                        id='modal-lane-geo-state'
                        label='Pickup State'
                        placeholder='Enter state'
                        countries={['US']}
                        resultTypes={['State']}
                        value={{ state: value }}
                        onChange={(val: ILocationSuggestion) => {
                          if (val.state) {
                            onChange(val.state)
                          }
                        }}
                        errorMessage={errors.laneGeographicCriteriaPickupValue?.message}
                        invalid={!!errors.laneGeographicCriteriaPickupValue}
                        dataTest='modal-lane-geo-state'
                        large
                      />
                    )}
                    {laneGeographicCriteriaPickupType === GEOGRAPHIC_CRITERIA_TYPES.CITY_STATE && (
                      <SuggestLocation
                        id='modal-lane-geo-city-state'
                        label='Pickup City, State'
                        placeholder='Enter city, state'
                        resultTypes={['City']}
                        value={{
                          city: value?.split(', ')[0],
                          state: value?.split(', ')[1],
                        }}
                        onChange={(val: ILocationSuggestion) => {
                          if (val.city && val.state) {
                            onChange(`${val.city}, ${val.state}`)
                          }
                        }}
                        errorMessage={errors.laneGeographicCriteriaPickupValue?.message}
                        invalid={!!errors.laneGeographicCriteriaPickupValue}
                        dataTest='modal-lane-geo-city-state'
                        large
                      />
                    )}
                    {laneGeographicCriteriaPickupType === GEOGRAPHIC_CRITERIA_TYPES.DAT_MARKET && (
                      <Autocomplete
                        id='modal-lane-geo-dat-market'
                        label='Pickup DAT Market'
                        value={value}
                        type='dat_market'
                        onChange={datMarketHandler}
                        validationMessage={errors.laneGeographicCriteriaPickupValue?.message}
                        dataTest='modal-lane-geo-dat-market'
                        large
                      />
                    )}
                  </>
                )
              }}
            />
          </Box>
          <Box mb={2}>
            <Controller
              name='laneGeographicCriteriaDeliveryValue'
              control={control}
              render={({ onChange, value }) => {
                const datMarketHandler = ({
                  fullSelection,
                }: {
                  value: number
                  fullSelection?: { name: string }
                }) => {
                  onChange(fullSelection?.name || '')
                }
                return (
                  <>
                    {laneGeographicCriteriaDeliveryType === GEOGRAPHIC_CRITERIA_TYPES.REGION && (
                      <Select
                        label='Deivery Region'
                        placeholder='Enter region'
                        options={regionOptions}
                        value={value}
                        onChange={(opt: ISelectOption) => onChange(opt.value)}
                        invalid={!!errors.laneGeographicCriteriaDeliveryValue}
                        dataTest='modal-lane-geo-region'
                        large
                      />
                    )}
                    {laneGeographicCriteriaDeliveryType === GEOGRAPHIC_CRITERIA_TYPES.STATE && (
                      <SuggestLocation
                        id='modal-geo-state'
                        label='Delivery State'
                        placeholder='Enter state'
                        countries={['US']}
                        resultTypes={['State']}
                        value={{ state: value }}
                        onChange={(val: ILocationSuggestion) => {
                          if (val.state) {
                            onChange(val.state)
                          }
                        }}
                        errorMessage={errors.laneGeographicCriteriaDeliveryValue?.message}
                        invalid={!!errors.laneGeographicCriteriaDeliveryValue}
                        dataTest='modal-lane-geo-state'
                        large
                      />
                    )}
                    {laneGeographicCriteriaDeliveryType ===
                      GEOGRAPHIC_CRITERIA_TYPES.CITY_STATE && (
                      <SuggestLocation
                        id='modal-lane-geo-city-state'
                        label='Delivery City, State'
                        placeholder='Enter city, state'
                        resultTypes={['City']}
                        value={{
                          city: value?.split(', ')[0],
                          state: value?.split(', ')[1],
                        }}
                        onChange={(val: ILocationSuggestion) => {
                          if (val.city && val.state) {
                            onChange(`${val.city}, ${val.state}`)
                          }
                        }}
                        errorMessage={errors.laneGeographicCriteriaDeliveryValue?.message}
                        invalid={!!errors.laneGeographicCriteriaDeliveryValue}
                        dataTest='modal-lane-geo-city-state'
                        large
                      />
                    )}
                    {laneGeographicCriteriaDeliveryType ===
                      GEOGRAPHIC_CRITERIA_TYPES.DAT_MARKET && (
                      <Autocomplete
                        id='modal-lane-geo-dat-market'
                        label='Delivery DAT Market'
                        value={value}
                        type='dat_market'
                        onChange={datMarketHandler}
                        validationMessage={errors.laneGeographicCriteriaDeliveryValue?.message}
                        dataTest='modal-lane-geo-dat-market'
                        large
                      />
                    )}
                  </>
                )
              }}
            />
          </Box>
        </>
      )}
      {(ruleType === RULE_TYPES.GEOGRAPHIC || ruleType === RULE_TYPES.TIME) && (
        <Box mb={2} display='flex'>
          <Box mr={1} flex={1}>
            <DeprecatedControlledSelect
              control={control}
              name='dateType'
              SelectProps={{
                label: 'Date Type',
                options: ruleType === RULE_TYPES.GEOGRAPHIC ? dateGeoOptions : dateOnlyOptions,
                dataTest: 'date-type',
                required: true,
                large: true,
              }}
            />
          </Box>
        </Box>
      )}
      {ruleType === RULE_TYPES.STEP_PRICING && (
        <Box mb={2}>
          <Controller
            name='countLoadsQuotedCriteria'
            control={control}
            render={({ onChange, value }) => {
              const changeHandler = (rule: RecursivePartial<ISpotBiddingRule>) => {
                onChange(rule.countLoadsQuotedCriteria)
              }
              return (
                <CountLoadsQuotedValue
                  value={value}
                  onChange={changeHandler}
                  error={errors.countLoadsQuotedCriteria?.message}
                  errors={errors.countLoadsQuotedCriteria as ICountLoadsQuotedErrors}
                />
              )
            }}
          />
        </Box>
      )}
      {dateType === DATE_TYPES.NUMBER_OF_DAYS_OUT && (
        <Box display='flex' flexDirection='column'>
          <Box mb={1}>
            <Controller
              name='proximalHourCriteria'
              control={control}
              render={({ onChange, value }) => {
                const changeHandler = (rule: RecursivePartial<ISpotBiddingRule>) => {
                  const { rangeEnd: end, rangeStart: start } = rule.proximalHourCriteria || {}
                  onChange({
                    rangeStart: start,
                    rangeEnd: end,
                  })
                }
                return (
                  <NumDaysOut
                    value={value}
                    onChange={changeHandler}
                    error={errors.proximalHourCriteria?.message}
                  />
                )
              }}
            />
          </Box>
          <Box mb={1}>
            <ControlledCheckbox
              control={control}
              defaultValue={!!spotBiddingRule?.isSameDay}
              name='includeSameDay'
              CheckboxProps={{
                label: 'Same Day?',
                disabled: appointmentTime,
              }}
            />
          </Box>
        </Box>
      )}
      {(dateType === DATE_TYPES.SAME_DAY ||
        dateType === DATE_TYPES.NEXT_DAY ||
        (dateType === DATE_TYPES.NUMBER_OF_DAYS_OUT && includeSameDay)) && (
        <Box mb={2}>
          <Controller
            name='ruleStartTimeCriteria'
            control={control}
            render={({ onChange }) => {
              const changeTimeHandler = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
                setTime(value)
                onChange({
                  rangeStart: value,
                })
              }
              return (
                <TimeInput
                  label='Rule Start Time (EST)'
                  placeholder='Optional'
                  value={time}
                  onChange={changeTimeHandler}
                  large
                  timeFormat={TIME_INPUT_FORMAT.MILITARY}
                />
              )
            }}
          />
        </Box>
      )}
      {dateType === DATE_TYPES.NUMBER_OF_DAYS_OUT && (
        <Box mb={1}>
          <ControlledCheckbox
            control={control}
            defaultValue={!!spotBiddingRule?.appointmentTimeCriteria}
            name='appointmentTime'
            CheckboxProps={{
              label: 'Appointment?',
              disabled: includeSameDay,
            }}
          />
        </Box>
      )}
      {dateType === DATE_TYPES.NUMBER_OF_DAYS_OUT && appointmentTime && (
        <Box mb={2}>
          <DeprecatedControlledSelect
            control={control}
            name='appointmentType'
            SelectProps={{
              label: 'Appointment Type',
              options: APPOINTMENT_TYPE_OPTIONS,
              dataTest: 'date-type',
              required: true,
              large: true,
              invalid: !!errors.appointmentType,
              validationMessage: errors.appointmentType?.message,
            }}
          />
          {appointmentType && (
            <Controller
              name='appointmentTimeCriteria'
              control={control}
              render={({ onChange, value }) => {
                const changeHandler = (rule: RecursivePartial<ISpotBiddingRule>) => {
                  const {
                    deliveryRangeEnd: delRangeEnd,
                    deliveryRangeStart: delRangeStart,
                    pickupRangeEnd: puRangeEnd,
                    pickupRangeStart: pickupStart,
                    timezoneOverride: timezone,
                  } = rule.appointmentTimeCriteria || {}
                  onChange({
                    deliveryRangeEnd: delRangeEnd,
                    deliveryRangeStart: delRangeStart,
                    pickupRangeEnd: puRangeEnd,
                    pickupRangeStart: pickupStart,
                    timezoneOverride: timezone,
                  })
                }
                const newValue = { appointmentTimeCriteria: { ...value } }
                return (
                  <AppointmentTime
                    value={newValue}
                    type={appointmentType}
                    error={errors.appointmentTimeCriteria?.message}
                    onChange={changeHandler}
                  />
                )
              }}
            />
          )}
        </Box>
      )}
      {dateType === DATE_TYPES.DATE_RANGE && (
        <Box mb={2}>
          <ControlledRangeDatePicker
            name='dateCriteria'
            minDate={yesterday}
            invalid={
              !!errors?.dateCriteria?.rangeStart?.message ||
              !!errors?.dateCriteria?.rangeEnd?.message
            }
            validationMessage={
              errors?.dateCriteria?.rangeStart?.message || errors?.dateCriteria?.rangeEnd?.message
            }
            defaultValue={dates}
            control={control}
          />
        </Box>
      )}
      <Box mb={2}>
        <DeprecatedControlledTextInput
          control={control}
          name='reason'
          TextInputProps={{
            name: 'reason',
            label: 'Reason',
            placeholder: 'Reason',
            dataTest: 'reason',
            invalid: !!errors.reason,
            validationMessage: errors.reason?.message,
            required: true,
          }}
        />
      </Box>
    </FormModal>
  )
}
